import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import Button from "@/components/site/Button";
import useSeo from "@/hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const dagen = ["zondag", "maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag"];
    const maanden = [
        "januari", "februari", "maart", "april", "mei", "juni",
        "juli", "augustus", "september", "oktober", "november", "december",
    ];
    return `${dagen[d.getDay()]} ${d.getDate()} ${maanden[d.getMonth()]}, ${d.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Amsterdam" })}`;
}

export default function BetalingTerug() {
    useSeo({
        title: "Betaling controleren | Human Design Studio",
        description: "We controleren je betaling en bevestigen zodra alles vaststaat.",
        path: "/betaling/terug",
    });

    const [state, setState] = useState("checking"); // checking | paid | pending | canceled | unknown
    const [booking, setBooking] = useState(null);
    const pollsRef = useRef(0);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const bookingId = params.get("booking_id");
        if (!bookingId) {
            setState("unknown");
            return;
        }

        // Poll booking status; webhook fires async so we may need a few checks.
        let cancelled = false;
        const poll = async () => {
            try {
                const { data } = await axios.get(`${API}/bookings/${bookingId}/status`);
                if (cancelled) return;
                setBooking(data);
                if (data.status === "betaald") {
                    setState("paid");
                    return;
                }
                if (data.status === "geannuleerd") {
                    setState("canceled");
                    return;
                }
                // status still reservering -> keep polling briefly
                pollsRef.current += 1;
                if (pollsRef.current < 8) {
                    setTimeout(poll, 1500);
                } else {
                    setState("pending");
                }
            } catch {
                if (!cancelled) setState("unknown");
            }
        };
        poll();
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <section
            className="mx-auto max-w-[720px] px-5 md:px-10 pt-24 md:pt-40 pb-32 min-h-[70vh]"
            data-testid="page-betaling-terug"
        >
            {state === "checking" && (
                <div data-testid="betaling-checking">
                    <p className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)] mb-6">
                        Even geduld
                    </p>
                    <h1 className="font-[Fraunces] text-[36px] md:text-[48px] leading-tight mb-6" style={{ fontWeight: 300 }}>
                        We controleren je betaling
                    </h1>
                    <p className="text-[16px] leading-[1.65] text-[var(--hds-ink)]/80">
                        Dit duurt hoogstens een moment. Je hoeft niets te doen.
                    </p>
                </div>
            )}

            {state === "paid" && (
                <div data-testid="betaling-paid">
                    <p className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-green)] mb-6">
                        Bevestigd
                    </p>
                    <h1 className="font-[Fraunces] text-[36px] md:text-[52px] leading-tight mb-6" style={{ fontWeight: 300 }}>
                        Je afspraak staat vast
                    </h1>
                    {booking && (
                        <div className="rounded-[14px] bg-[var(--hds-card)] border border-[var(--hds-line)] p-6 md:p-8 mb-8">
                            <p className="text-[12px] tracking-[0.18em] uppercase text-[var(--hds-ink)]/55 mb-3">
                                {booking.aanbod_naam}
                            </p>
                            <p className="font-[Fraunces] text-[22px] md:text-[26px] leading-tight text-[var(--hds-ink)]" style={{ fontWeight: 400 }}>
                                {fmtDate(booking.starttijd)}
                            </p>
                            <p className="text-[14px] text-[var(--hds-ink)]/60 mt-2">
                                {booking.duur} minuten. Online via Google Meet.
                            </p>
                        </div>
                    )}
                    <p className="text-[16px] leading-[1.65] text-[var(--hds-ink)]/80 mb-8">
                        Je ontvangt zo een bevestigingsmail met de Google Meet-link
                        en de details van de sessie.
                    </p>
                    <Button variant="primary" icon="arrow" to="/" data-testid="betaling-home-btn">
                        Terug naar home
                    </Button>
                </div>
            )}

            {state === "canceled" && (
                <div data-testid="betaling-canceled">
                    <p className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)] mb-6">
                        Betaling niet afgerond
                    </p>
                    <h1 className="font-[Fraunces] text-[36px] md:text-[48px] leading-tight mb-6" style={{ fontWeight: 300 }}>
                        Je afspraak staat nog niet vast
                    </h1>
                    <p className="text-[16px] leading-[1.65] text-[var(--hds-ink)]/80 mb-8">
                        De betaling is niet afgerond. Je afspraak staat nog niet vast.
                        Wil je het opnieuw proberen? Kies een moment dat bij je past.
                    </p>
                    <div className="flex flex-wrap gap-3">
                        <Button variant="primary" icon="arrow" to="/boeken" data-testid="betaling-retry-btn">
                            Opnieuw proberen
                        </Button>
                        <Button variant="secondary" to="/contact">
                            Neem contact op
                        </Button>
                    </div>
                </div>
            )}

            {state === "pending" && (
                <div data-testid="betaling-pending">
                    <p className="text-[11.5px] tracking-[0.28em] uppercase text-[var(--hds-clay)] mb-6">
                        Nog even
                    </p>
                    <h1 className="font-[Fraunces] text-[32px] md:text-[42px] leading-tight mb-6" style={{ fontWeight: 300 }}>
                        We wachten nog op bevestiging van je bank
                    </h1>
                    <p className="text-[16px] leading-[1.65] text-[var(--hds-ink)]/80 mb-8">
                        Ververs deze pagina over een paar minuten. Zodra de
                        betaling binnen is, ontvang je een mail met de bevestiging.
                    </p>
                    <Link to="/" className="underline underline-offset-2 text-[var(--hds-clay)]">
                        Terug naar home
                    </Link>
                </div>
            )}

            {state === "unknown" && (
                <div data-testid="betaling-unknown">
                    <h1 className="font-[Fraunces] text-[32px] md:text-[42px] leading-tight mb-6" style={{ fontWeight: 300 }}>
                        We konden je boeking niet vinden
                    </h1>
                    <p className="text-[16px] leading-[1.65] text-[var(--hds-ink)]/80 mb-8">
                        Neem contact op als je zeker weet dat de betaling geslaagd is,
                        dan zoeken we het samen uit.
                    </p>
                    <Button variant="primary" icon="arrow" to="/contact">
                        Neem contact op
                    </Button>
                </div>
            )}
        </section>
    );
}
